import { createContext, useContext, useState, useEffect } from "react";
import { auth } from "../firebase.config.js";
import {
   signInWithEmailAndPassword,
   signInWithPopup,
   OAuthProvider,
   signOut as firebaseSignOut,
   sendPasswordResetEmail,
   onAuthStateChanged,
   setPersistence,
   browserSessionPersistence,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import useFetchData from "../components/useFetchData";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
   const [isAuthenticated, setIsAuthenticated] = useState(false);
   const [firebaseUser, setFirebaseUser] = useState(null);
   const [authReady, setAuthReady] = useState(false);
   const [userClaims, setUserClaims] = useState(null);
   const [orgInfo, setOrgInfo] = useState(null);
   const [challengeData, setChallengeData] = useState(null);
   const [errorMessage, setErrorMessage] = useState(null);
   const [loading, setLoading] = useState(true);
   const navigate = useNavigate();
   const { fetchData } = useFetchData();

   const verifyTokenWithBackend = async (idToken) => {
      try {
         const response = await fetch("/verifytoken.sz", {
            method: "POST",
            headers: {
               "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({ idToken }),
            credentials: "include",
         });

         if (!response.ok) {
            return { valid: false, payload: null };
         }

         const result = await response.json();
         if (result?.st !== 1) {
            return { valid: false, payload: null };
         }
         return { valid: true, payload: result?.payload?.[0] || null };
      } catch (error) {
         return { valid: false, payload: null };
      }
   };

   // Derived state: orgId from userClaims
   

   // Monitor authentication state
   useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
         setFirebaseUser(user || null); 
         if (user) {
            // Always fetch fresh token result to ensure we have latest claims
            const idTokenResult = await user.getIdTokenResult(true);

            // Validate backend session when restoring persisted Firebase auth.
            // Without this, /dashboard/home can stay visible until another API call fails.
            const backendSession = await verifyTokenWithBackend(idTokenResult.token);
            if (!backendSession.valid) {
               await firebaseSignOut(auth);
               setFirebaseUser(null);
               setIsAuthenticated(false);
               setAuthReady(false);
               setUserClaims(null);
               setOrgInfo(null);
               sessionStorage.removeItem("idToken");
               setLoading(false);
               return;
            }

            const freshClaims = {
               userId: user.uid,
               email: user.email,
               name: user.displayName,
               phoneNumber: user.phoneNumber,
               ...idTokenResult.claims,
            };

            const sessionPayload = backendSession.payload;
            setOrgInfo(sessionPayload ? {
               orgExternalId: sessionPayload.o_id || null,
               orgBucketId: sessionPayload.org_bucket_id || null,
               iconId: sessionPayload.icon_id || null,
            } : null);
            // Always update claims when user exists (for page refresh, token refresh, etc.)
            setUserClaims(freshClaims);
            setIsAuthenticated(true);
            setAuthReady(true); // Auth is ready whenever we have a valid authenticated user
            sessionStorage.setItem("idToken", idTokenResult.token);

            console.log("AuthContext: User authenticated with claims:", freshClaims);
         } else {
            // User logged out
            setIsAuthenticated(false);
            setAuthReady(false);
            setUserClaims(null);
            setOrgInfo(null);
            sessionStorage.removeItem("idToken");
         }
         setLoading(false);
      });

      // Cleanup subscription on unmount
      return () => unsubscribe();
   }, []);

   // Authenticate user with email and password
   const authenticate = async (email, password) => {
      try {
         setErrorMessage(null);
         setAuthReady(false); // Reset auth ready state

         // Keep auth scoped to the browser tab/session.
         // After tab/browser close, user must sign in again.
         await setPersistence(auth, browserSessionPersistence);

         const userCredential = await signInWithEmailAndPassword(auth, email, password);
         const user = userCredential.user;

         // Don't set isAuthenticated here - let handleSuccessfulLogin do it after backend verification
         await handleSuccessfulLogin(user);
      } catch (err) {
         // Handle specific exceptions
         if (err.code === "auth/user-not-found") {
            setErrorMessage("No user found with this email.");
         } else if (err.code === "auth/wrong-password") {
            setErrorMessage("Invalid password.");
         } else if (err.code === "auth/too-many-requests") {
            setErrorMessage("Too many login attempts. Try again later.");
         } else {
            setErrorMessage(err.message || "An unknown error occurred.");
         }
         setAuthReady(false);
      }
   };

   const authenticateFederated = async (providerId) => {
      try {
         setErrorMessage(null);
         setAuthReady(false);
 
         // Keep federated login scoped to the browser tab/session,
         // same as email/password.
         await setPersistence(auth, browserSessionPersistence);
 
         const provider = new OAuthProvider(providerId);
         const userCredential = await signInWithPopup(auth, provider);
 
         // Reuse the exact same Rekon backend validation flow.
         await handleSuccessfulLogin(userCredential.user);
 
         return { st: 1 };
      } catch (err) {
         console.error("Federated login error:", err);
 
         if (err.code === "auth/popup-closed-by-user") {
            setErrorMessage("Login window was closed before completion. Please try again.");
         } else if (err.code === "auth/account-exists-with-different-credential") {
            setErrorMessage(
               "This email already exists using another sign-in method. Please sign in with your existing method first."
            );
         } else {
            setErrorMessage(err.message || "Federated login failed.");
         }
 
         setAuthReady(false);
         return { st: -1, payload: err.message || "Federated login failed." };
      }
   };

   // Handle various challenges based on response type
   const handleChallenge = (response, email) => {
      setChallengeData({ challengeName: response.ChallengeName, data: { ...response, Session: response.Session }, email });
      switch (response.ChallengeName) {
         case "FORGOT_PASSWORD":
            navigate("/pages/forgotpassword", { state: { email } });
            break;
         case "NEW_PASSWORD_REQUIRED":
            navigate("/pages/newpassword", { state: { email } });
            break;
         case "SMS_MFA":
            navigate("/pages/mfa", { state: { email } });
            break;
         case "MFA_SETUP":
            // Navigate to MFA setup page if needed
            navigate("/pages/setup-mfa", { state: { email } });
            break;
         default:
            console.warn("Unhandled challenge:", response.ChallengeName);
            break;
      }
   };

   // Complete FORGOT_PASSWORD challenge
   // const completeResetPasswordChallenge = async (verificationCode, email, newPassword) => {
   //    try {
   //       const command = new ConfirmForgotPasswordCommand({
   //          ClientId: process.env.VITE_CLIENT_ID,
   //          Username: email,
   //          ConfirmationCode: verificationCode, // PIN code received by the user
   //          Password: newPassword, // New password to set
   //       });
   //       const response = await client.send(command);
   //       navigate("/pages/signin3", {
   //          state: {
   //             successMessage: "Password reset successfully. You can now log in with your new password.",
   //          },
   //       });
   //       //navigate("/pages/login3");
   //    } catch (err) {
   //       console.error("Error completing new password challenge:", err);
   //       setErrorMessage(err.message || "Failed to set new password.");
   //    }
   // };

   // Complete FORGOT_PASSWORD challenge
   const triggerForgotPasswordChallenge = async (email) => {
      try {
         await sendPasswordResetEmail(auth, email);

         // Clear any previous error message
         setErrorMessage(null);

         // Redirect with success message
         navigate("/pages/forgotpassword", {
            state: {
               successMessage: `Password reset link sent to ${email}.`,
               email: email,
            },
         });
      } catch (err) {
         console.error("Error sending reset password email:", err);
         setErrorMessage(err.message || "Failed to send reset email.");
      }
   };

   // Complete NEW_PASSWORD_REQUIRED challenge
   // const completeNewPasswordChallenge = async (newPassword) => {
   //    if (!challengeData || !challengeData.data.Session) {
   //       console.error("Session parameter is missing. Unable to complete new password challenge.");
   //       return;
   //    }

   //    try {
   //       const command = new RespondToAuthChallengeCommand({
   //          ClientId: process.env.VITE_CLIENT_ID,
   //          ChallengeName: challengeData.challengeName,
   //          Session: challengeData.data.Session,
   //          ChallengeResponses: {
   //             USERNAME: challengeData.email,
   //             NEW_PASSWORD: newPassword,
   //          },
   //       });

   //       const response = await client.send(command);

   //       if (response.AuthenticationResult) {
   //          handleSuccessfulLogin(response.AuthenticationResult);
   //       }
   //    } catch (err) {
   //       console.error("Error completing new password challenge:", err);
   //       setErrorMessage(err.message || "Failed to set new password.");
   //    }
   // };

   // Complete SMS MFA challenge
   // const completeMFAChallenge = async (code) => {
   //    if (!challengeData) return;

   //    try {
   //       const command = new RespondToAuthChallengeCommand({
   //          ClientId: process.env.VITE_CLIENT_ID,
   //          ChallengeName: "SMS_MFA",
   //          Session: challengeData.data.Session,
   //          ChallengeResponses: {
   //             USERNAME: challengeData.email,
   //             SMS_MFA_CODE: code,
   //          },
   //       });

   //       const response = await client.send(command);

   //       if (response.AuthenticationResult) {
   //          handleSuccessfulLogin(response.AuthenticationResult);
   //       }
   //    } catch (err) {
   //       console.error("Error completing MFA challenge:", err);
   //       setErrorMessage(err.message || "Failed to verify MFA code.");
   //    }
   // };

   const verifyToken = async (idToken) => {
      console.log("AUTHRESULT:", idToken);
      //const idToken = authResult["IdToken"];
      const url = "/verifytoken.sz";
      const method = "POST";
      const body = { idToken: idToken };
      const contentType = "application/x-www-form-urlencoded";
      try {
         const { responseData, fetchLoading, fetchError } = await fetchData(url, method, body, contentType);
         if (responseData.st === 1) {
            return {
               st: responseData.st,
               payload: responseData.payload,
            };
         } else {
            return { st: -1, payload: responseData.m_en || "Authentication error. Please try again later." };
         }
      } catch (error) {
         return { st: -1, payload: "Error during verification. Please try again." };
      }
   };

   // Handle successful login
   const handleSuccessfulLogin = async (authResult) => {
      try {
         // Step 1: Call backend verification with current token
         const currentIdToken = await authResult.getIdToken();
         const verifyResponse = await verifyToken(currentIdToken);

         if (verifyResponse.st !== 1) {
            // Verification failed
            setErrorMessage(verifyResponse.payload || "Token verification failed. Please try again.");
            setAuthReady(false);
            return;
         }

         const sessionPayload = verifyResponse?.payload?.[0] || null;
         setOrgInfo(sessionPayload ? {
            orgExternalId: sessionPayload.o_id || null,
            orgBucketId: sessionPayload.org_bucket_id || null,
            iconId: sessionPayload.icon_id || null,
         } : null);

         // Step 2: Force token refresh to get fresh claims from backend
         // This ensures we get any updated custom claims set by the backend
         const freshIdTokenResult = await authResult.getIdTokenResult(true);
         const freshClaims = {
            userId: authResult.uid,
            email: authResult.email,
            name: authResult.displayName,
            phoneNumber: authResult.phoneNumber,
            ...freshIdTokenResult.claims,
         };

         // Step 3: Update state with fresh claims
         setUserClaims(freshClaims);
         setIsAuthenticated(true);
         setAuthReady(true);

         // Step 4: Store fresh token and navigate
         sessionStorage.setItem("idToken", freshIdTokenResult.token);
         navigate("/dashboard/home");

      } catch (error) {
         console.error("Error during successful login handling:", error);
         setErrorMessage("An error occurred during token verification. Please try again later.");
         setAuthReady(false);
      }
   };


   // Sign out user
   const signOut = async () => {
      try {
         await firebaseSignOut(auth);
         setIsAuthenticated(false);
         setAuthReady(false);
         setUserClaims(null);
         setOrgInfo(null);
         sessionStorage.removeItem("idToken");
         navigate("/pages/signin3");
      } catch (err) {
         console.error("Error signing out:", err);
         setErrorMessage(err.message || "Failed to sign out. Please try again.");
      }
   };

   return (
      <AuthContext.Provider
         value={{
            authenticate,
            authenticateFederated,
            // completeNewPasswordChallenge,
            // completeResetPasswordChallenge,
            triggerForgotPasswordChallenge,
            // completeMFAChallenge,
            isAuthenticated,
            authReady,
            firebaseUser,
            userClaims,
            orgInfo,
            loading,
            errorMessage,
            setErrorMessage,
            signOut,
         }}>
         {children}
      </AuthContext.Provider>
   );
};

export const useAuth = () => useContext(AuthContext);
